// Preview renderer for invitation email templates.
//
// This mirrors render() in channel-gate/internal/outreach/email_template.go —
// the service that actually sends the mail. Keep the two in step: if a token
// or a rule changes there, change it here, or the Channels template preview
// starts lying about what recipients receive. The variable list itself is
// fetched from ChannelGate (GET /outreach/email-template).

/** Fallback copy, used only when ChannelGate cannot be reached. */
export const FALLBACK_DEFAULT_TEMPLATE = {
    subject: "You're invited to take {{survey_name}}",
    body_text: `{{org_logo}}
Hello {{recipient_name}},

You have been invited to participate in {{survey_name}}.

Please click the link below to begin:
{{survey_link}}

Thank you for your time.

{{org_name}}`,
    variables: [
        { token: '{{recipient_name}}', description: "Recipient's name (falls back to their email address)", sample: 'Jane Doe' },
        { token: '{{survey_name}}', description: 'Title of the survey being sent', sample: 'Employee Engagement 2026' },
        { token: '{{survey_link}}', description: 'Personalized URL that opens the survey', sample: 'https://survey.samsurveyor.com/d/deploy-abc123?r=…' },
        { token: '{{org_name}}', description: "Your organization's formal name", sample: 'Your Organization' },
        { token: '{{org_logo}}', description: "Your organization's logo image. Renders as a picture in HTML mail; omitted from the plain-text copy", sample: '' },
        { token: '{{org_logo_url}}', description: "Raw URL of your organization's logo, for use in your own HTML", sample: '' },
    ],
};

/**
 * Per-type fallback copy, mirroring the constants in
 * channel-gate/internal/outreach/email_template.go. Used only when ChannelGate
 * cannot be reached; the editor prefers the live copy from the endpoint so the
 * two can never drift silently.
 */
export const FALLBACK_DEFAULT_TEMPLATES = {
    invitation: FALLBACK_DEFAULT_TEMPLATE,
    reminder: {
        subject: 'A reminder: {{survey_name}} is still open',
        body_text: `{{org_logo}}
Hello {{recipient_name}},

We recently invited you to take part in {{survey_name}}, and it is still open.

If you have a few minutes, you can begin here:
{{survey_link}}

If you would rather not take part, you can ignore this message.

Thank you,

{{org_name}}`,
        variables: FALLBACK_DEFAULT_TEMPLATE.variables,
    },
    completion: {
        subject: 'Your responses to {{survey_name}}',
        body_text: `{{org_logo}}
Hello {{recipient_name}},

Thank you for completing {{survey_name}}. Your answers are set out below for
your records.`,
        // No {{survey_link}}: the survey is over, and a link back into it would
        // only lead to a refusal.
        variables: FALLBACK_DEFAULT_TEMPLATE.variables.filter(v => v.token !== '{{survey_link}}'),
    },
};

/** The fallback copy for one type, defaulting to the invitation. */
export const fallbackDefaultFor = (templateType) =>
    FALLBACK_DEFAULT_TEMPLATES[templateType] || FALLBACK_DEFAULT_TEMPLATE;

/**
 * Storage convention for completion templates, mirroring ResultsSentinel in
 * channel-gate/internal/outreach/email_template.go.
 *
 * A completion template is two authored halves with the generated record of the
 * answers between them. Both halves live in the one body_text column separated
 * by this marker, so no schema change was needed. The author never sees it: the
 * editor shows an Opening box and a Closing box.
 */
export const RESULTS_SENTINEL = '[[SAMSURVEYOR:RESULTS]]';

const stripSentinel = (text) => String(text || '').split(RESULTS_SENTINEL).join('');

/**
 * A body with no sentinel is all opening and no closing — which is exactly what
 * templates stored before this convention existed look like, and exactly what
 * they already produced, since the answers were appended at the end.
 */
export function splitCompletionBody(bodyText) {
    const text = String(bodyText || '');
    const index = text.indexOf(RESULTS_SENTINEL);
    if (index < 0) return { opening: text, closing: '' };
    return {
        opening: stripSentinel(text.slice(0, index)).replace(/\n+$/, ''),
        closing: stripSentinel(text.slice(index + RESULTS_SENTINEL.length)).replace(/^\n+/, ''),
    };
}

/** The inverse. Only the sentinel is stripped, so typing is never interfered with. */
export function joinCompletionBody(opening, closing) {
    return `${stripSentinel(opening)}\n\n${RESULTS_SENTINEL}\n\n${stripSentinel(closing)}`;
}

const escapeHTML = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Collapse the gaps left by variables that resolved to nothing. */
const tidy = (s) => s.replace(/\n{3,}/g, '\n\n').trim();

const substitute = (source, pairs) =>
    Object.entries(pairs).reduce(
        (acc, [token, value]) => acc.split(token).join(value),
        source,
    );

/** Render a template the way ChannelGate will. */
export function renderEmailPreview(template, facts) {
    const subject = template.subject || '';
    const bodyText = template.body_text || '';
    const bodyHTML = template.body_html || '';

    const textPairs = {
        '{{recipient_name}}': facts.recipientName,
        '{{survey_name}}': facts.surveyName,
        '{{survey_link}}': facts.surveyLink,
        '{{org_name}}': facts.orgName,
        '{{org_logo}}': '',
        '{{org_logo_url}}': facts.logoURL,
    };

    const out = {
        subject: tidy(substitute(subject, textPairs)),
        bodyText: tidy(substitute(bodyText, textPairs)),
        bodyHTML: '',
    };

    const wantsLogo = bodyText.includes('{{org_logo}}') && !!facts.logoURL;
    if (!bodyHTML && !wantsLogo) return out;

    const source = bodyHTML || (
        `<div style="white-space:pre-wrap;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:15px;line-height:1.6;color:#202124;">` +
        escapeHTML(bodyText) +
        `</div>`
    );

    const logoIMG = facts.logoURL
        ? `<img src="${escapeHTML(facts.logoURL)}" alt="${escapeHTML(facts.orgName || 'Logo')}" style="max-height:64px;max-width:240px;margin-bottom:16px;border:0;">`
        : '';

    out.bodyHTML = substitute(source, {
        '{{recipient_name}}': escapeHTML(facts.recipientName),
        '{{survey_name}}': escapeHTML(facts.surveyName),
        '{{survey_link}}': `<a href="${escapeHTML(facts.surveyLink)}">${escapeHTML(facts.surveyLink)}</a>`,
        '{{org_name}}': escapeHTML(facts.orgName),
        '{{org_logo}}': logoIMG,
        '{{org_logo_url}}': escapeHTML(facts.logoURL),
    });
    return out;
}

/** Sample values for the preview, drawn from the real org where possible. */
export function previewFacts(org, variables) {
    const sampleOf = (token, fallback) =>
        (variables || []).find(v => v.token === token)?.sample || fallback;

    return {
        recipientName: sampleOf('{{recipient_name}}', 'Jane Doe'),
        surveyName: sampleOf('{{survey_name}}', 'Employee Engagement 2026'),
        surveyLink: sampleOf('{{survey_link}}', 'https://survey.samsurveyor.com/d/deploy-abc123?r=…'),
        orgName: org?.formal_name || org?.name || sampleOf('{{org_name}}', 'Your Organization'),
        logoURL: org?.logo_url || '',
    };
}
